import BaseModel, { isBaseModel } from '../baseModel';
import { errorToString } from '../errorToString';
import genGuid from '../guid';
import { isPromise } from '../isPromise';
import Bus, { BusDebug } from './bus';
import type { StandardSchemaV1 } from '@standard-schema/spec';

export interface BusSubscribeCallback<T> {
    (data: T, topic: string, baseModel: BaseModel<T>): void;
}

export interface BusSubscribeOptions {
    correlationGuid?: string;
    debug?: BusDebug;
    measureTime?: boolean;
    signal?: AbortSignal;
}

export function bus_subscribe<T = unknown>(
    this: Bus,
    topic: string,
    callback: BusSubscribeCallback<T>,
    options?: BusSubscribeOptions,
): () => void;

export function bus_subscribe<T = unknown>(
    this: Bus,
    topic: string,
    schema: StandardSchemaV1<unknown, T>,
    callback: BusSubscribeCallback<T>,
    options?: BusSubscribeOptions,
): () => void;

export function bus_subscribe<T = unknown>(
    this: Bus,
    topic: string,
    ...args: [BusSubscribeCallback<T>, BusSubscribeOptions?] | [StandardSchemaV1<unknown, T>, BusSubscribeCallback<T>, BusSubscribeOptions?]
) {
    let schema: StandardSchemaV1<unknown, T> | undefined;
    let callback: BusSubscribeCallback<T>;
    let options: BusSubscribeOptions | undefined;

    if (typeof args[0] === 'function') {
        [callback, options] = args as [BusSubscribeCallback<T>, BusSubscribeOptions?];
    } else {
        [schema, callback, options] = args as [StandardSchemaV1<unknown, T>, BusSubscribeCallback<T>, BusSubscribeOptions?];
    }

    const { correlationGuid, debug = this.options.debug, measureTime, signal } = options ?? {};

    let handle: (() => void) | undefined;
    const startTime = performance.now();

    const _callback = async (pkg: any, currentTopic = topic) => {
        if (correlationGuid && pkg.correlationGuid !== correlationGuid) return;
        if (!pkg) {
            console.error(`Payload missing for topic ${currentTopic}`);
            return;
        }

        const time = measureTime ? ` (${Math.round(performance.now() - startTime)}ms)` : '';
        let baseModel: BaseModel<T>;

        if (isBaseModel(pkg)) {
            baseModel = pkg as BaseModel<T>;
        } else {
            const guid = genGuid();
            baseModel = {
                guid,
                correlationGuid: correlationGuid ?? guid,
                ts: new Date().toISOString(),
                data: pkg,
            };
        }

        let data: T = baseModel.data as T;

        if (schema) {
            let result = schema['~standard'].validate(baseModel.data);

            if (isPromise(result)) {
                result = await result;
            }

            if (result.issues) {
                console.error(`Validation failed for topic ${currentTopic}:`, {
                    data: baseModel.data,
                    issues: result.issues,
                    get baseModel() {
                        return baseModel;
                    },
                });
                return;
            }

            baseModel.data = result.value;
            data = result.value;
        }

        debug?.(`Received ${currentTopic}${time}`, data, baseModel);

        try {
            callback(data, currentTopic, baseModel);
        } catch (error) {
            console.error(`Error in callback for topic ${currentTopic}:`, errorToString(error), baseModel, error);
        }
    };

    const subscription = {
        start: () => {
            handle = this.channelIn?.subscribe(topic, _callback);
        },
        stop: () => {
            handle?.();
        },
    };

    subscription.start();
    this.subscriptions.add(subscription);

    const cancel = () => {
        subscription.stop();
        this.subscriptions.delete(subscription);
    };

    signal?.addEventListener('abort', cancel);
    return cancel;
}
